import { endGatewaySession } from "@api/auth-requests";
import {
	HANDOFF_CODE_PARAM,
	HANDOFF_PARTNER_PARAM,
} from "@constants/constants";
import { partnerIdInstance } from "@utils/partner-id-instance";
import { tokenInstance } from "@utils/token-instance";

import { exchangeHandoffCode } from "../api/exchange-handoff-code";

let pending: Promise<void> | null = null;

const signInWithCode = async (
	code: string,
	partner: string | null,
): Promise<void> => {
	try {
		const { active_token, refresh_token } = await exchangeHandoffCode(code);

		// Half a pair would be refreshed with the previous session's token,
		// and quietly turn back into whoever that belonged to.
		if (!active_token || !refresh_token) return;

		// The new session replaces this browser's; nothing holds the old one
		// any more, so the gateway should not keep it either.
		void endGatewaySession(tokenInstance.getToken());

		tokenInstance.setToken(active_token);
		tokenInstance.setRefreshToken(refresh_token);

		if (partner) partnerIdInstance.setHandedOverPartner(partner);
	} catch {
		// The gateway answers 401 alike for an expired, a spent and a made-up
		// code, and each ends the same way: storage as it was, the usual sign-in.
	}
};

/**
 * Takes the one-time code another zypl app sent the user here with, on
 * whatever path, and starts trading it for a session. The organization sent
 * along with it (`?partner=`) is opened once the session is in.
 *
 * Must run before the router reads the address: route redirects (`/`, the
 * guest layout) would otherwise carry the user off without it, and in SPA
 * mode no route hook runs in the browser early enough to catch it. The code
 * leaves the address and its history entry straight away, before the
 * exchange settles.
 */
export const beginHandoff = (): void => {
	if (typeof window === "undefined") return;

	const url = new URL(window.location.href);
	if (!url.searchParams.has(HANDOFF_CODE_PARAM)) return;

	const code = url.searchParams.get(HANDOFF_CODE_PARAM);
	const partner = url.searchParams.get(HANDOFF_PARTNER_PARAM);
	url.searchParams.delete(HANDOFF_CODE_PARAM);
	url.searchParams.delete(HANDOFF_PARTNER_PARAM);
	window.history.replaceState(window.history.state, "", url);

	if (!code) return;

	const run: Promise<void> = signInWithCode(code, partner).finally(() => {
		if (pending === run) pending = null;
	});
	pending = run;
};

/** The exchange `beginHandoff` started, while it is still in flight. */
export const pendingHandoff = (): Promise<void> | null => pending;
